<template>
  <form class="tool-card" @submit.prevent="onSubmit">
    <div class="form-section">
      <div class="form-section-header">
        <div class="num">1</div>
        <h3><span>{{ t('form.section1.title') }}</span></h3>
      </div>

      <div class="field-row">
        <div class="field-group field-postcode">
          <label for="postcode">{{ t('form.postcode.label') }}</label>
          <input type="text" id="postcode" v-model="postcode" :placeholder="t('form.postcode.placeholder')" maxlength="7" @blur="resolveAddress" :class="{ invalid: errors.postcode }">
          <p v-if="errors.postcode" class="field-error">{{ errors.postcode }}</p>
        </div>
        <div class="field-group field-huisnummer">
          <label for="huisnummer">{{ t('form.huisnummer.label') }}</label>
          <input type="text" id="huisnummer" v-model="huisnummer" :placeholder="t('form.huisnummer.placeholder')" inputmode="numeric" @blur="resolveAddress" :class="{ invalid: errors.huisnummer }">
          <p v-if="errors.huisnummer" class="field-error">{{ errors.huisnummer }}</p>
        </div>
        <div class="field-group field-toevoeging">
          <label for="toevoeging">{{ t('form.toevoeging.label') }} <span class="label-optional">{{ t('form.toevoeging.optional') }}</span></label>
          <input type="text" id="toevoeging" v-model="toevoeging" :placeholder="t('form.toevoeging.placeholder')" maxlength="10" @blur="resolveAddress">
        </div>
      </div>

      <div v-if="addressLoading" class="address-resolved loading">
        {{ t('form.addressLoading') }}
      </div>
      <div v-else-if="resolvedAddress" class="address-resolved">
        {{ resolvedAddress }}
      </div>
      <div v-if="errors.address" class="field-error">{{ errors.address }}</div>
    </div>

    <div class="form-section">
      <div class="form-section-header">
        <div class="num">2</div>
        <h3><span>{{ t('form.section2.title') }}</span> <span class="h-sub">{{ t('form.section2.subtitle') }}</span></h3>
      </div>

      <div class="field-group">
        <label>{{ t('form.nhg.label') }}</label>
        <p class="hint">{{ t('form.nhg.hint') }}</p>
        <div class="toggle-row">
          <button type="button" class="toggle-btn" :class="{ active: nhg === false }" @click="nhg = false">{{ t('form.nhg.no') }}</button>
          <button type="button" class="toggle-btn" :class="{ active: nhg === true }" @click="nhg = true">{{ t('form.nhg.yes') }}</button>
        </div>
        <div v-if="nhg" class="nhg-notice">
          {{ t('form.nhg.notice') }}
        </div>
      </div>

      <div class="field-group">
        <label for="verstrekker">{{ t('form.verstrekker.label') }}</label>
        <select id="verstrekker" v-model="verstrekkerKey" :class="{ invalid: errors.verstrekker }">
          <option value="">{{ t('form.verstrekker.placeholder') }}</option>
          <option v-for="(v, key) in sortedVerstrekkers" :key="key" :value="key">{{ v.naam }}</option>
        </select>
        <p v-if="errors.verstrekker" class="field-error">{{ errors.verstrekker }}</p>
      </div>


      <div class="field-group">
        <label for="schuld">{{ t('form.schuld.label') }}</label>
        <p class="hint">{{ t('form.schuld.hint') }}</p>
        <input type="text" id="schuld" v-model="schuldDisplay" :placeholder="t('form.schuld.placeholder')" inputmode="numeric" @input="formatSchuld" :class="{ invalid: errors.schuld }">
        <p v-if="errors.schuld" class="field-error">{{ errors.schuld }}</p>
      </div>
    </div>

    <div v-if="wozFallback" class="woz-fallback">
      <div class="form-section-header">
        <div class="num">!</div>
        <h3><span>{{ t('form.wozFallback.title') }}</span> <span class="h-sub">{{ t('form.wozFallback.subtitle') }}</span></h3>
      </div>
      <p class="hint" v-html="t('form.wozFallback.hint')"></p>
      <div class="field-group">
        <label for="wozManual">{{ t('form.wozManual.label') }}</label>
        <input type="text" id="wozManual" v-model="wozManualDisplay" :placeholder="t('form.wozManual.placeholder')" inputmode="numeric" @input="formatWoz">
      </div>
    </div>

    <button type="submit" class="submit-btn" :disabled="loading || nhg">
      <template v-if="loading">
        <span class="spinner"></span> {{ t('form.submit.loading') }}
      </template>
      <template v-else>
        {{ t('form.submit.calculate') }}
      </template>
    </button>
    <p v-if="errors.woz && !wozFallback" class="field-error" style="text-align:center; margin-top:12px">{{ errors.woz }}</p>
    <p class="tool-disclaimer" v-html="'<strong>' + t('form.disclaimer').split('.')[0] + '.</strong> ' + t('form.disclaimer').split('.').slice(1).join('.')">
    </p>
  </form>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import verstrekkers from '@/data/verstrekkers.json'
import { useBerekening } from '@/composables/useBerekening'
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()

const emit = defineEmits(['result'])

const postcode = ref('')
const huisnummer = ref('')
const toevoeging = ref('')
const resolvedAddress = ref('')
const addressLoading = ref(false)
const verstrekkerKey = ref('')
const schuldDisplay = ref('')
const nhg = ref(false)
const loading = ref(false)
const errors = ref({})
const wozFallback = ref(false)
const wozManualDisplay = ref('')

let resolveTimer = null

watch(postcode, () => { errors.value = { ...errors.value, postcode: null, address: null } })
watch(huisnummer, () => { errors.value = { ...errors.value, huisnummer: null, address: null } })
watch(toevoeging, () => {
  if (resolvedAddress.value || toevoeging.value.trim()) resolveAddress()
})
watch(verstrekkerKey, () => { errors.value = { ...errors.value, verstrekker: null } })
watch(schuldDisplay, () => { errors.value = { ...errors.value, schuld: null } })
watch(wozManualDisplay, () => { errors.value = { ...errors.value, woz: null } })

const { berekenResultaat, formatEuro, formatRange } = useBerekening()

const sortedVerstrekkers = computed(() => {
  const entries = Object.entries(verstrekkers)
  entries.sort((a, b) => a[1].naam.localeCompare(b[1].naam, 'nl'))
  return Object.fromEntries(entries)
})

function formatCurrency(e, setter) {
  let v = e.target.value.replace(/[^\d]/g, '')
  e.target.value = v ? '€ ' + parseInt(v).toLocaleString('nl-NL') : ''
  setter(e.target.value)
}

function formatSchuld(e) { formatCurrency(e, v => { schuldDisplay.value = v }) }
function formatWoz(e) { formatCurrency(e, v => { wozManualDisplay.value = v }) }

async function resolveAddress() {
  const pc = postcode.value.replace(/\s/g, '')
  const nr = huisnummer.value.trim()
  if (pc.length < 6 || !nr) {
    resolvedAddress.value = ''
    return
  }

  clearTimeout(resolveTimer)
  resolveTimer = setTimeout(async () => {
    if (resolvedAddress.value || toevoeging.value.trim()) addressLoading.value = true
    errors.value = { ...errors.value, address: null }
    try {
      const res = await fetch(`/api/address-lookup?postcode=${encodeURIComponent(pc)}&number=${encodeURIComponent(nr)}&addition=${encodeURIComponent(toevoeging.value.trim())}`)
      const data = await res.json()
      if (data.address) {
        resolvedAddress.value = data.address
      } else {
        resolvedAddress.value = ''
        if (toevoeging.value.trim()) {
          errors.value = { ...errors.value, address: t('form.error.address') }
        }
      }
    } catch {
      resolvedAddress.value = ''
    } finally {
      addressLoading.value = false
    }
  }, 300)
}

function validate() {
  const e = {}
  const pc = postcode.value.replace(/\s/g, '')
  if (!pc || pc.length < 6) {
    e.postcode = t('form.error.postcode')
  }
  if (!huisnummer.value.trim()) {
    e.huisnummer = t('form.error.huisnummer')
  }
  if (!resolvedAddress.value) {
    e.address = t('form.error.addressInvalid')
  }
  if (!verstrekkerKey.value) {
    e.verstrekker = t('form.error.verstrekker')
  }
  const schuldRaw = schuldDisplay.value.replace(/[^\d]/g, '')
  if (!schuldRaw || parseInt(schuldRaw) < 1000) {
    e.schuld = t('form.error.schuld')
  }
  errors.value = e
  return Object.keys(e).length === 0
}

async function onSubmit() {
  if (!validate()) return

  const schuld = parseInt(schuldDisplay.value.replace(/[^\d]/g, ''))
  const vKey = verstrekkerKey.value
  const verstrekker = verstrekkers[vKey]

  if (wozFallback.value) {
    const wozRaw = wozManualDisplay.value.replace(/[^\d]/g, '')
    if (!wozRaw || parseInt(wozRaw) < 10000) {
      errors.value = { ...errors.value, woz: t('form.error.woz') }
      return
    }
    const wozWaarde = parseInt(wozRaw)
    const resultaat = berekenResultaat(schuld, wozWaarde, verstrekker)
    fetch('/api/counters/berekeningen', { method: 'POST' }).catch(() => {})
    emit('result', {
      verstrekker,
      verstrekkerKey: vKey,
      woz: { waarde: wozWaarde, peildatum: null, success: false },
      adres: resolvedAddress.value,
      schuld,
      ...resultaat,
      heeft_email: !!verstrekker.email,
      formatEuro,
      formatRange,
    })
    return
  }

  loading.value = true
  errors.value = { ...errors.value, woz: null }

  try {
    const res = await fetch('/api/woz-lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address: resolvedAddress.value }),
    })
    const data = await res.json()

    if (!data.wozWaarde) {
      wozFallback.value = true
      return
    }

    const wozWaarde = data.wozWaarde
    const resultaat = berekenResultaat(schuld, wozWaarde, verstrekker)
    fetch('/api/counters/berekeningen', { method: 'POST' }).catch(() => {})

    emit('result', {
      verstrekker,
      verstrekkerKey: vKey,
      woz: { waarde: wozWaarde, peildatum: data.peildatum, success: true },
      adres: resolvedAddress.value,
      schuld,
      ...resultaat,
      heeft_email: !!verstrekker.email,
      formatEuro,
      formatRange,
    })
  } catch {
    wozFallback.value = true
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.tool-card {
  max-width: 720px;
  margin: 0 auto;
  background: var(--bg-card);
  border: 1px solid var(--line-soft);
  padding: 48px;
  border-radius: 28px;
  box-shadow: var(--shadow-soft);
}
@media (max-width: 600px) { .tool-card { padding: 28px; border-radius: 20px; } }

.form-section {
  padding-bottom: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid var(--line-soft);
}
.form-section:last-of-type { border-bottom: none; padding-bottom: 0; margin-bottom: 36px; }
.form-section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
}
.form-section-header .num {
  background: var(--ink);
  color: var(--bg);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Fraunces', serif;
  font-size: 15px;
  font-weight: 800;
  flex-shrink: 0;
}
.form-section-header h3 {
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.025em;
}
.form-section-header .h-sub {
  font-size: 13px;
  color: var(--ink-muted);
  margin-left: 6px;
  font-weight: 400;
}

.field-group { margin-bottom: 20px; }
.field-group:last-child { margin-bottom: 0; }
.field-group label {
  display: block;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
  color: var(--ink);
}
.field-group .hint {
  font-size: 13px;
  color: var(--ink-muted);
  margin-bottom: 8px;
  line-height: 1.5;
}
.field-group .hint :deep(a) {
  font-weight: 600;
}
.field-group input,
.field-group select {
  width: 100%;
  padding: 14px 16px;
  font-size: 17px;
  font-family: inherit;
  border: 1.5px solid var(--line-soft);
  background: var(--bg);
  border-radius: 12px;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  color: var(--ink);
}
.field-group select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 42px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1.5L6 6.5L11 1.5' stroke='%2314497F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 16px center;
  cursor: pointer;
}
.field-group input:focus,
.field-group select:focus {
  background: var(--bg-card);
  border-color: var(--blue);
  box-shadow: 0 0 0 3px rgba(31, 118, 214, 0.15);
}

.label-optional {
  font-weight: 400;
  color: var(--ink-muted);
  font-size: 13px;
  letter-spacing: 0;
  margin-left: 4px;
}

.toggle-row {
  display: flex;
  gap: 8px;
}
.toggle-btn {
  flex: 1;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  border: 1.5px solid var(--line-soft);
  background: var(--bg);
  border-radius: 12px;
  cursor: pointer;
  color: var(--ink-soft);
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}
.toggle-btn.active {
  border-color: var(--blue);
  background: var(--blue-pale);
  color: var(--blue-dark);
}
.nhg-notice {
  margin-top: 12px;
  background: var(--yellow);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.5;
}

.submit-btn {
  width: 100%;
  background: var(--blue);
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  padding: 20px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: var(--shadow-soft);
  position: relative;
  overflow: hidden;
}
.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
  background: var(--blue-dark);
}
.submit-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
  transition: left 0.6s;
}
.submit-btn:hover::before {
  left: 100%;
}
.tool-disclaimer {
  text-align: center;
  font-size: 13px;
  color: var(--ink-muted);
  margin-top: 20px;
}
.tool-disclaimer :deep(strong) { color: var(--ink); }

.field-error {
  color: #c0392b;
  font-size: 13px;
  margin-top: 6px;
  font-weight: 600;
}
.field-group input.invalid,
.field-group select.invalid {
  border-color: #c0392b;
  background: #fdf2f2;
}

.verstrekker-hint {
  background: var(--blue-pale);
  border: 1px solid var(--blue-light);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 14px;
  color: var(--ink-soft);
  line-height: 1.5;
  margin-bottom: 20px;
}

.field-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;
}
@media (max-width: 500px) {
  .field-row { grid-template-columns: 1fr 1fr; }
  .field-postcode { grid-column: 1 / -1; }
}

.address-resolved {
  background: var(--bg-soft);
  border: 1px solid var(--line-soft);
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 15px;
  color: var(--ink);
  font-weight: 600;
  margin-top: 12px;
}
.address-resolved.loading {
  color: var(--ink-muted);
  font-weight: 400;
  font-style: italic;
}

.submit-btn:disabled {
  background: var(--ink-muted);
  cursor: wait;
  transform: none;
  box-shadow: none;
}
.submit-btn:disabled:hover {
  transform: none;
  box-shadow: none;
  background: var(--ink-muted);
}
.submit-btn:disabled::before {
  display: none;
}

.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.woz-fallback {
  background: #fdf2f2;
  border: 1.5px solid #e74c3c;
  border-radius: 16px;
  padding: 24px 28px 32px !important;
  margin-bottom: 24px;
}
.woz-fallback .num {
  background: #e74c3c !important;
}
.woz-fallback .hint {
  margin-bottom: 16px;
}
.woz-fallback .hint :deep(a) {
  font-weight: 700;
  color: var(--blue);
}
.woz-fallback .field-group {
  margin-bottom: 0;
}
</style>
